import React, { useState, useEffect, useRef } from 'react';
import { db, app } from '../../../src/admin/servicios/firebase';
import { doc, onSnapshot, updateDoc, setDoc } from 'firebase/firestore';
import { FaUser, FaCreditCard, FaExchangeAlt, FaExclamationTriangle } from 'react-icons/fa';
import { loadStripe } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';
import { getFunctions, httpsCallable } from 'firebase/functions';
import CheckoutForm from './CheckoutForm';
import { GiSteeringWheel } from "react-icons/gi";

const stripePromise = loadStripe("pk_live_51OgLwXAxbLnR5YvC3Gpxzugwlts5h18SI5QNkcDH5py2Uk5N00ZeDQK4VhOOz0GIw8GbQBsEwYBuvgGuUFdWpT7k0083jOCFUV", {
  stripeAccount: "acct_1T1r4kBDR9gWPsvs"
});

const SEAT_MAP = [
  ['conductor', 'p', 'a1', 'a2'],
  ['a3', 'a4', 'p', 'puerta'],
  ['a5', 'a6', 'p', 'puerta'],
  ['a7', 'a8', 'p', 'a9'],
  ['a10', 'a11', 'p', 'a12'],
  ['a13', 'a14', 'a15', 'a16'],
];

const SESSION_ID = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

const toSafeInt = (val) => {
  const n = parseInt(val, 10);
  return isNaN(n) ? 0 : n;
};

const formatTime = (secs) =>
  `${Math.floor(secs / 60)}:${(secs % 60).toString().padStart(2, '0')}`;

function AsientoTimer({ expiresAt }) {
  const [remaining, setRemaining] = useState(0);
  useEffect(() => {
    if (!expiresAt) return;
    const update = () => {
      const diff = Math.max(0, Math.floor((new Date(expiresAt) - Date.now()) / 1000));
      setRemaining(diff);
    };
    update();
    const t = setInterval(update, 1000);
    return () => clearInterval(t);
  }, [expiresAt]);
  if (!remaining) return null;
  return (
    <span className="absolute -top-2 -right-2 bg-orange-500 text-white text-[9px] font-black rounded-full px-1 py-0.5 leading-none shadow-lg z-20">
      {formatTime(remaining)}
    </span>
  );
}

export default function Reservar() {
  const [viaje, setViaje] = useState(null);
  const [esRedondo, setEsRedondo] = useState(false);

  const viajeIdaIdRef = useRef(null);
  const viajeRegresoIdRef = useRef(null);

  const [asientosOcupados, setAsientosOcupados] = useState([]);
  const [asientosBloqueados, setAsientosBloqueados] = useState([]);
  const [asientosOcupadosRegreso, setAsientosOcupadosRegreso] = useState([]);
  const [asientosBloqueadosRegreso, setAsientosBloqueadosRegreso] = useState([]);

  const [tempSelecciones, setTempSelecciones] = useState({});
  const [tempSeleccionesRegreso, setTempSeleccionesRegreso] = useState({});

  const [seleccionados, setSeleccionados] = useState([]);
  const [seleccionadosRegreso, setSeleccionadosRegreso] = useState([]);
  const [mostrandoVan, setMostrandoVan] = useState('ida');

  const [datosPasajeros, setDatosPasajeros] = useState({});
  const [contacto, setContacto] = useState({ telefono: '' });
  const [loading, setLoading] = useState(true);

  const [clientSecret, setClientSecret] = useState("");
  const [procesando, setProcesando] = useState(false);
  const [alerta, setAlerta] = useState({ mostrar: false, mensaje: '', tipo: 'error' });

  const [tiempoRestante, setTiempoRestante] = useState(null);
  const [expirado, setExpirado] = useState(false);

  const cleanupRef = useRef([]);

  useEffect(() => {
    const cleanup = () => cleanupRef.current.forEach(fn => fn());
    window.addEventListener('beforeunload', cleanup);
    return () => { cleanup(); window.removeEventListener('beforeunload', cleanup); };
  }, []);


  // ─── TEMPORALES — updateDoc con dot notation ──────────────────────────────
  const marcarTemporal = async (viajeId, asientoId) => {
    if (!viajeId) return;
    const expiresAt = new Date(Date.now() + 10 * 60 * 1000).toISOString();
    try {
      await updateDoc(doc(db, 'viajes', viajeId), {
        [`tempSelecciones.${asientoId}`]: {
          sessionId: SESSION_ID,
          expiresAt,
          timestamp: new Date().toISOString()
        }
      });
      const cleanupFn = () => limpiarTemporal(viajeId, asientoId);
      cleanupRef.current = [...cleanupRef.current, cleanupFn];
    } catch (e) {
      console.warn('No se pudo marcar temporal:', e.message);
    }
  };

  const limpiarTemporal = async (viajeId, asientoId) => {
    if (!viajeId) return;
    try {
      await updateDoc(doc(db, 'viajes', viajeId), {
        [`tempSelecciones.${asientoId}`]: null
      });
    } catch (e) { }
  };

  const limpiarTodasTemporales = async () => {
    try {
      const idaId = viajeIdaIdRef.current;
      const regresoId = viajeRegresoIdRef.current;

      if (idaId) {
        const mios = Object.entries(tempSelecciones)
          .filter(([, v]) => v?.sessionId === SESSION_ID).map(([k]) => k);
        if (mios.length > 0) {
          const updates = {};
          mios.forEach(id => { updates[`tempSelecciones.${id}`] = null; });
          await updateDoc(doc(db, 'viajes', idaId), updates);
        }
      }

      if (regresoId) {
        const miosR = Object.entries(tempSeleccionesRegreso)
          .filter(([, v]) => v?.sessionId === SESSION_ID).map(([k]) => k);
        if (miosR.length > 0) {
          const updatesR = {};
          miosR.forEach(id => { updatesR[`tempSelecciones.${id}`] = null; });
          await updateDoc(doc(db, 'viajes', regresoId), updatesR);
        }
      }
    } catch (e) { }
  };

  const parsearYLimpiarTemps = async (viajeId, ts, ocupados) => {
    const ahora = Date.now();
    const aLimpiar = {};
    const activos = {};

    Object.entries(ts).forEach(([asientoId, v]) => {
      const yaVendido = ocupados.includes(asientoId);
      const esTaquilla = v?.esTaquilla === true;
      const exp = !v || (!esTaquilla && new Date(v.expiresAt) <= ahora);
      if (yaVendido || exp) {
        aLimpiar[`tempSelecciones.${asientoId}`] = null;
      } else {
        activos[asientoId] = v;
      }
    });

    if (Object.keys(aLimpiar).length > 0) {
      updateDoc(doc(db, 'viajes', viajeId), aLimpiar).catch(() => { });
    }
    return activos;
  };

  // ─── LISTENERS en tiempo real ─────────────────────────────────────────────
  useEffect(() => {
    const info = JSON.parse(localStorage.getItem('viajeSeleccionado'));
    if (!info) { window.location.href = '/'; return; }

    console.log('📦 viajeSeleccionado:', info);

    const parseSnap = (data) => ({
      ocupados: (data.ocupados || []).map(item => typeof item === 'object' ? item.asiento : item),
      bloqueados: (data.bloqueados || []).map(item => typeof item === 'object' ? item.asiento : item),
      tempSelecciones: data.tempSelecciones || {},
    });

    const setupListeners = async () => {
      try {
        if (info.esRedondo && info.viajeIda && info.viajeRegreso) {
          setEsRedondo(true);
          setViaje(info);

          const idaId = info.viajeIda.id;
          const regresoId = info.viajeRegreso.id;
          viajeIdaIdRef.current = idaId;
          viajeRegresoIdRef.current = regresoId;

          const unsubIda = onSnapshot(doc(db, 'viajes', idaId), async (snap) => {
            if (snap.exists()) {
              const { ocupados, bloqueados, tempSelecciones: ts } = parseSnap(snap.data());
              setAsientosOcupados(ocupados);
              setAsientosBloqueados(bloqueados);
              const activos = await parsearYLimpiarTemps(idaId, ts, ocupados);
              setTempSelecciones(activos);
            } else {
              setAsientosOcupados([]);
              setAsientosBloqueados([]);
              setTempSelecciones({});
            }
          });

          const unsubRegreso = onSnapshot(doc(db, 'viajes', regresoId), async (snap) => {
            if (snap.exists()) {
              const { ocupados, bloqueados, tempSelecciones: ts } = parseSnap(snap.data());
              setAsientosOcupadosRegreso(ocupados);
              setAsientosBloqueadosRegreso(bloqueados);
              const activos = await parsearYLimpiarTemps(regresoId, ts, ocupados);
              setTempSeleccionesRegreso(activos);
            } else {
              setAsientosOcupadosRegreso([]);
              setAsientosBloqueadosRegreso([]);
              setTempSeleccionesRegreso({});
            }
            setLoading(false);
          });

          return () => { unsubIda(); unsubRegreso(); };

        } else {
          setEsRedondo(false);
          setViaje(info);

          const idaId = info.id;
          viajeIdaIdRef.current = idaId;

          const unsub = onSnapshot(doc(db, 'viajes', idaId), async (snap) => {
            if (snap.exists()) {
              const { ocupados, bloqueados, tempSelecciones: ts } = parseSnap(snap.data());
              setAsientosOcupados(ocupados);
              setAsientosBloqueados(bloqueados);
              const activos = await parsearYLimpiarTemps(idaId, ts, ocupados);
              setTempSelecciones(activos);
            } else {
              setAsientosOcupados([]);
              setAsientosBloqueados([]);
              setTempSelecciones({});
            }
            setLoading(false);
          });

          return () => unsub();
        }
      } catch (error) {
        console.error('Error en setupListeners:', error);
        setLoading(false);
      }
    };

    setupListeners();
  }, []);

  // ─── CONTADOR ─────────────────────────────────────────────────────────────
  useEffect(() => {
    const expiry = localStorage.getItem('expiryTimestamp');
    if (expiry) {
      const remaining = Math.max(0, Math.floor((new Date(expiry) - new Date()) / 1000));
      if (remaining > 0) setTiempoRestante(remaining);
      else limpiarSeleccion();
    }
  }, []);

  useEffect(() => {
    const haySeleccion = esRedondo
      ? seleccionados.length > 0 || seleccionadosRegreso.length > 0
      : seleccionados.length > 0;

    if (haySeleccion && tiempoRestante === null && !expirado) {
      const expiry = new Date(Date.now() + 10 * 60 * 1000);
      localStorage.setItem('expiryTimestamp', expiry.toISOString());
      setTiempoRestante(600);
    }

    if (!haySeleccion && tiempoRestante !== null) {
      localStorage.removeItem('expiryTimestamp');
      setTiempoRestante(null);
      setExpirado(false);
    }
  }, [seleccionados, seleccionadosRegreso, esRedondo, tiempoRestante, expirado]);

  useEffect(() => {
    if (tiempoRestante === null) return;
    const intervalo = setInterval(() => {
      setTiempoRestante(prev => {
        if (prev <= 1) {
          clearInterval(intervalo);
          localStorage.removeItem('expiryTimestamp');
          limpiarSeleccion();
          setExpirado(true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(intervalo);
  }, [tiempoRestante]);

  const limpiarSeleccion = () => {
    limpiarTodasTemporales();
    setSeleccionados([]);
    setSeleccionadosRegreso([]);
    setDatosPasajeros({});
    setTiempoRestante(null);
    setExpirado(false);
    localStorage.removeItem('expiryTimestamp');
    mostrarAlerta('⏰ Tiempo de reserva agotado. Vuelve a seleccionar asientos.', 'error');
  };

  // ─── TOGGLE ASIENTO ───────────────────────────────────────────────────────
  const toggleAsiento = (id) => {
    if (clientSecret) return;

    const viajeId = esRedondo
      ? (mostrandoVan === 'ida' ? viajeIdaIdRef.current : viajeRegresoIdRef.current)
      : viajeIdaIdRef.current;

    const estaReservado = (temp) =>
      temp && temp.sessionId !== SESSION_ID &&
      (temp.esTaquilla === true || new Date(temp.expiresAt) > Date.now());

    if (esRedondo) {
      if (mostrandoVan === 'ida') {
        if (asientosOcupados.includes(id) || asientosBloqueados.includes(id)) return;
        if (estaReservado(tempSelecciones[id])) return;
        setSeleccionados(prev => {
          const sel = !prev.includes(id);
          if (sel) { marcarTemporal(viajeId, id); return [...prev, id]; }
          else { limpiarTemporal(viajeId, id); return prev.filter(a => a !== id); }
        });
      } else {
        if (asientosOcupadosRegreso.includes(id) || asientosBloqueadosRegreso.includes(id)) return;
        if (estaReservado(tempSeleccionesRegreso[id])) return;
        setSeleccionadosRegreso(prev => {
          const sel = !prev.includes(id);
          if (sel) { marcarTemporal(viajeId, id); return [...prev, id]; }
          else { limpiarTemporal(viajeId, id); return prev.filter(a => a !== id); }
        });
      }
    } else {
      if (asientosOcupados.includes(id) || asientosBloqueados.includes(id)) return;
      if (estaReservado(tempSelecciones[id])) return;
      setSeleccionados(prev => {
        const sel = !prev.includes(id);
        if (sel) { marcarTemporal(viajeId, id); return [...prev, id]; }
        else { limpiarTemporal(viajeId, id); return prev.filter(a => a !== id); }
      });
    }
  };

  const handlePasajeroChange = (asientoId, nombre) => {
    setDatosPasajeros(prev => ({ ...prev, [asientoId]: nombre }));
  };

  const calcularTotal = () => {
    if (!viaje) return 0;
    if (esRedondo) return toSafeInt(viaje.precioTotal) * seleccionados.length;
    return toSafeInt(viaje.precio) * seleccionados.length;
  };

  const totalPagar = calcularTotal();

  const mostrarAlerta = (mensaje, tipo = 'error') => {
    setAlerta({ mostrar: true, mensaje, tipo });
    setTimeout(() => setAlerta({ mostrar: false, mensaje: '', tipo: 'error' }), 4000);
  };

  // ─── CHECKOUT ─────────────────────────────────────────────────────────────
  const handleCheckout = async () => {
    if (!esRedondo && seleccionados.length === 0) return mostrarAlerta("Selecciona al menos un asiento.");
    if (esRedondo && (seleccionados.length === 0 || seleccionadosRegreso.length === 0))
      return mostrarAlerta("Debes seleccionar asientos tanto para ida como para regreso.");
    if (esRedondo && seleccionados.length !== seleccionadosRegreso.length)
      return mostrarAlerta("Debes seleccionar la misma cantidad de asientos para ida y regreso.");

    const nombresIncompletos = seleccionados.some(id => !datosPasajeros[id] || datosPasajeros[id].trim() === "");
    if (nombresIncompletos) return mostrarAlerta("Por favor, ingresa los nombres de todos los pasajeros.");
    if (!contacto.telefono) return mostrarAlerta("Faltan datos de contacto (teléfono).");

    localStorage.removeItem('expiryTimestamp');
    setTiempoRestante(null);
    setExpirado(false);
    setProcesando(true);

    try {
      const functionsInstance = getFunctions(app, 'us-central1');
      const crearIntento = httpsCallable(functionsInstance, 'crearIntentoPago');

      const payload = esRedondo ? {
        cantidadAsientos: toSafeInt(seleccionados.length),
        viajeId: viajeIdaIdRef.current,
        esRedondo: true,
        viajeRegresoId: viajeRegresoIdRef.current,
        monto: toSafeInt(totalPagar),
        asientosIda: seleccionados,
        asientosRegreso: seleccionadosRegreso,
        pasajeros: datosPasajeros,
        contacto: { telefono: contacto.telefono || '' },
        tipoViaje: 'redondo',
        rutaIda: { origen: viaje.origenIda, destino: viaje.destinoIda },
        fechaIda: viaje.fechaIda,
        horaIda: viaje.horaSalidaIda,
        rutaRegreso: { origen: viaje.origenRegreso, destino: viaje.destinoRegreso },
        fechaRegreso: viaje.fechaRegreso,
        horaRegreso: viaje.horaSalidaRegreso,
      } : {
        cantidadAsientos: toSafeInt(seleccionados.length),
        viajeId: viajeIdaIdRef.current,
        monto: toSafeInt(totalPagar),
        asientosIda: seleccionados,
        pasajeros: datosPasajeros,
        contacto: { telefono: contacto.telefono || '' },
        tipoViaje: 'sencillo',
        ruta: { origen: viaje.origen, destino: viaje.destino },
        fechaViaje: viaje.fecha,
        horaViaje: viaje.horaSalida,
      };

      console.log('🚀 Payload enviado a Cloud Function:', payload);

      const result = await crearIntento(payload);
      setClientSecret(result.data.clientSecret);

    } catch (error) {
      console.error("❌ ERROR AL INICIAR PAGO:", error);
      mostrarAlerta(`Error: ${error.message}`);
    } finally {
      setProcesando(false);
    }
  };

  // ─── RENDER ───────────────────────────────────────────────────────────────
  if (loading) return (
    <div className="h-screen flex items-center justify-center font-bold text-blue-600">
      Cargando van...
    </div>
  );

  const asientosActuales = esRedondo && mostrandoVan === 'regreso' ? asientosOcupadosRegreso : asientosOcupados;
  const bloqueadosActuales = esRedondo && mostrandoVan === 'regreso' ? asientosBloqueadosRegreso : asientosBloqueados;
  const seleccionadosActuales = esRedondo && mostrandoVan === 'regreso' ? seleccionadosRegreso : seleccionados;
  const tempActuales = esRedondo && mostrandoVan === 'regreso' ? tempSeleccionesRegreso : tempSelecciones;

  const sesionesActivas = new Set(
    Object.values(tempActuales)
      .filter(v => v && v.sessionId !== SESSION_ID &&
        (v.esTaquilla === true || new Date(v.expiresAt) > Date.now()))
      .map(v => v.sessionId)
  ).size;

  return (
    <div className="min-h-screen bg-slate-50 p-4 md:p-10 font-sans text-slate-900">

      {alerta.mostrar && (
        <div className="fixed top-4 left-1/2 transform -translate-x-1/2 z-50 animate-in slide-in-from-top-4 fade-in">
          <div className={`flex items-center gap-3 px-6 py-4 rounded-xl shadow-2xl border-l-4 max-w-md ${alerta.tipo === 'error'
            ? 'bg-red-50 border-red-500 text-red-800'
            : 'bg-blue-50 border-blue-500 text-blue-800'}`}>
            <FaExclamationTriangle className={`text-2xl ${alerta.tipo === 'error' ? 'text-red-500' : 'text-blue-500'}`} />
            <div className="flex-1"><p className="font-bold text-sm">{alerta.mensaje}</p></div>
            <button onClick={() => setAlerta({ ...alerta, mostrar: false })} className="text-gray-400 hover:text-gray-600 font-bold text-xl">×</button>
          </div>
        </div>
      )}

      <div className="max-w-6xl mx-auto">

        {esRedondo && (
          <div className="mb-6 bg-gradient-to-r from-blue-600 to-purple-600 text-white p-6 rounded-3xl shadow-xl">
            <h2 className="text-2xl font-black mb-4 flex items-center gap-2">
              <FaExchangeAlt /> Boleto Redondo - ${toSafeInt(viaje.precioTotal)} por persona
            </h2>
            <div className="grid md:grid-cols-2 gap-4 text-sm">
              <div className="bg-white/10 p-4 rounded-xl">
                <p className="font-bold mb-2">📍 IDA</p>
                <p>{viaje.origenIda} → {viaje.destinoIda}</p>
                <p>{viaje.fechaIda} • {viaje.horaSalidaIda}</p>
              </div>
              <div className="bg-white/10 p-4 rounded-xl">
                <p className="font-bold mb-2">📍 REGRESO</p>
                <p>{viaje.origenRegreso} → {viaje.destinoRegreso}</p>
                <p>{viaje.fechaRegreso} • {viaje.horaSalidaRegreso}</p>
              </div>
            </div>
          </div>
        )}

        {tiempoRestante !== null && (
          <div className={`mb-4 p-4 rounded-xl text-center font-bold text-lg shadow-lg ${tiempoRestante < 60
            ? 'bg-red-100 text-red-700 animate-pulse border-2 border-red-400'
            : 'bg-yellow-100 text-yellow-800 border-2 border-yellow-400'}`}>
            ⏳ Tiempo para completar la compra: {formatTime(tiempoRestante)} minutos
          </div>
        )}

        {sesionesActivas > 0 && (
          <div className="mb-4 p-3 bg-orange-50 border-2 border-orange-300 rounded-xl text-center text-orange-700 font-bold text-sm animate-pulse">
            👀 {sesionesActivas} {sesionesActivas === 1 ? 'persona está' : 'personas están'} viendo esta van ahora mismo
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">

          <div className="bg-white p-8 rounded-3xl shadow-2xl border border-slate-100">

            {esRedondo && (
              <div className="flex gap-2 mb-6">
                <button onClick={() => setMostrandoVan('ida')}
                  className={`flex-1 py-3 px-6 rounded-xl font-bold transition-all ${mostrandoVan === 'ida' ? 'bg-blue-600 text-white shadow-lg' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}>
                  🔵 Ida ({seleccionados.length})
                </button>
                <button onClick={() => setMostrandoVan('regreso')}
                  className={`flex-1 py-3 px-6 rounded-xl font-bold transition-all ${mostrandoVan === 'regreso' ? 'bg-purple-600 text-white shadow-lg' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}>
                  🟣 Regreso ({seleccionadosRegreso.length})
                </button>
              </div>
            )}

            <div className="flex flex-wrap justify-center gap-4 mb-8 text-xs font-bold uppercase tracking-wider text-slate-500">
              <div className="flex items-center gap-2"><div className="w-4 h-4 rounded bg-white border-2 border-emerald-500"></div> Disponible</div>
              <div className="flex items-center gap-2"><div className="w-4 h-4 rounded bg-blue-600 ring-2 ring-blue-100"></div> Tuyo</div>
              <div className="flex items-center gap-2"><div className="w-4 h-4 rounded bg-orange-400"></div> Otro cliente</div>
              <div className="flex items-center gap-2"><div className="w-4 h-4 rounded bg-slate-800 opacity-40"></div> Ocupado</div>
            </div>

            <div className="relative mx-auto max-w-[320px] border-4 border-slate-800 rounded-[40px] p-6 pb-12 bg-white shadow-2xl">
              <div className="absolute top-0 left-1/2 -translate-x-1/2 w-48 h-4 bg-slate-800 rounded-b-xl z-10"></div>

              <div className="grid gap-y-4 mt-8">
                {SEAT_MAP.map((fila, i) => (
                  <div key={i} className="flex justify-between items-center h-12">
                    {fila.map((asiento, j) => {
                      if (asiento === 'p') return <div key={j} className="w-12"></div>;
                      if (asiento === 'conductor') return (
                        <div key={j} className="w-12 h-12 flex items-center justify-center text-slate-400">
                          <GiSteeringWheel size={40} />
                        </div>
                      );
                      if (asiento === 'puerta') return (
                        <div key={j} className="w-12 flex items-center justify-center">
                          <span className="text-[10px] font-black text-slate-300 uppercase -rotate-90 tracking-widest">Acceso</span>
                        </div>
                      );

                      const ocupado = asientosActuales.includes(asiento);
                      const bloqueado = bloqueadosActuales.includes(asiento);
                      const selMio = seleccionadosActuales.includes(asiento);
                      const tempData = tempActuales[asiento];

                      const selOtroOnline = tempData &&
                        tempData.sessionId !== SESSION_ID &&
                        !tempData.esTaquilla &&
                        tempData.expiresAt &&
                        new Date(tempData.expiresAt) > Date.now();

                      const selTaquilla = tempData &&
                        tempData.sessionId !== SESSION_ID &&
                        tempData.esTaquilla === true;

                      const selOtro = selOtroOnline || selTaquilla;

                      return (
                        <div key={asiento} className="relative">
                          {selOtroOnline && <AsientoTimer expiresAt={tempData.expiresAt} />}
                          {selTaquilla && (
                            <span className="absolute -top-2 -right-2 bg-yellow-500 text-white text-[9px] font-black rounded-full px-1 py-0.5 leading-none shadow-lg z-20">T</span>
                          )}
                          <button
                            onClick={() => toggleAsiento(asiento)}
                            disabled={ocupado || bloqueado || !!clientSecret || selOtro}
                            title={
                              selTaquilla ? '🏪 Siendo atendido en taquilla'
                                : selOtroOnline ? `⏳ Seleccionado por otro cliente`
                                  : selMio ? 'Click para deseleccionar'
                                    : 'Disponible'
                            }
                            className={`w-12 h-12 rounded-lg flex items-center justify-center font-black text-sm transition-all shadow-sm
                              ${ocupado ? 'bg-slate-800 text-white opacity-40 cursor-not-allowed'
                                : bloqueado ? 'bg-red-100 text-red-500 cursor-not-allowed'
                                  : selTaquilla ? 'bg-yellow-400 text-white cursor-not-allowed opacity-90'
                                    : selOtroOnline ? 'bg-orange-400 text-white cursor-not-allowed opacity-80 animate-pulse'
                                      : selMio ? 'bg-blue-600 text-white ring-4 ring-blue-200 scale-110 z-10'
                                        : 'bg-white border-2 border-emerald-500 text-emerald-600 hover:bg-emerald-50'}`}
                          >
                            {ocupado ? 'X' : selTaquilla ? 'T' : selOtroOnline ? '⏳' : asiento.replace('a', '')}
                          </button>
                        </div>
                      );
                    })}
                  </div>
                ))}
              </div>
            </div>

            <p className="text-center mt-6 text-slate-400 text-sm font-bold uppercase">
              {esRedondo ? `Frente de la unidad - ${mostrandoVan === 'ida' ? 'IDA' : 'REGRESO'}` : 'Frente de la unidad'}
            </p>
          </div>

          <div className="flex flex-col gap-6">
            <div className="bg-white p-6 rounded-3xl shadow-xl max-h-[600px] overflow-y-auto">
              <h3 className="text-lg font-bold mb-4 flex gap-2 items-center sticky top-0 bg-white pb-2">
                <FaUser /> Datos de Pasajeros
              </h3>

              {seleccionados.length === 0 && (
                <p className="text-gray-400 text-center italic">Selecciona asientos en el mapa</p>
              )}

              {seleccionados.length > 0 && (
                <div className="mb-4">
                  {esRedondo && (
                    <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                      <p className="text-sm font-bold text-blue-800 mb-1">📋 Resumen:</p>
                      <p className="text-xs text-blue-700">
                        {seleccionados.length} pasajero{seleccionados.length > 1 ? 's' : ''} •
                        ${toSafeInt(viaje?.precioTotal)} × {seleccionados.length} = ${totalPagar} MXN total
                      </p>
                    </div>
                  )}

                  {seleccionados.map((asientoIda, index) => {
                    const asientoRegreso = seleccionadosRegreso[index];
                    return (
                      <div key={asientoIda} className="mb-4 p-4 bg-slate-50 rounded-xl border-l-4 border-blue-500 animate-in fade-in">
                        <label className="text-sm font-bold text-slate-700 mb-2 block">
                          Pasajero #{index + 1}
                          {esRedondo && asientoRegreso && (
                            <span className="ml-2 text-xs text-slate-500">
                              (Ida: {asientoIda.replace('a', '')} • Regreso: {asientoRegreso.replace('a', '')})
                            </span>
                          )}
                          {!esRedondo && (
                            <span className="ml-2 text-xs text-slate-500">
                              (Asiento {asientoIda.replace('a', '')})
                            </span>
                          )}
                        </label>
                        <input
                          className="w-full border-2 border-slate-200 p-3 focus:border-blue-500 outline-none bg-white rounded-lg"
                          placeholder="Nombre completo del pasajero"
                          disabled={!!clientSecret}
                          value={datosPasajeros[asientoIda] || ''}
                          onChange={(e) => handlePasajeroChange(asientoIda, e.target.value)}
                        />
                      </div>
                    );
                  })}
                </div>
              )}

              <div className="mt-4 sticky bottom-0 bg-white pt-2">
                <input
                  type="tel"
                  placeholder="WhatsApp"
                  disabled={!!clientSecret}
                  className="w-full border-2 p-3 rounded-lg bg-white focus:border-blue-500 outline-none"
                  value={contacto.telefono}
                  onChange={e => setContacto({ ...contacto, telefono: e.target.value })}
                />
              </div>
            </div>

            <div className="bg-blue-900 text-white p-8 rounded-3xl shadow-xl relative overflow-hidden">
              <div className="flex justify-between items-end mb-6">
                <div>
                  <p className="text-blue-300 text-xs font-bold">TOTAL A PAGAR</p>
                  <h4 className="text-5xl font-black">${totalPagar} MXN</h4>
                  {esRedondo && seleccionados.length > 0 && (
                    <p className="text-blue-300 text-sm mt-2">
                      {seleccionados.length} boleto{seleccionados.length !== 1 ? 's' : ''} redondo{seleccionados.length !== 1 ? 's' : ''} • ${toSafeInt(viaje?.precioTotal)} c/u
                    </p>
                  )}
                  {!esRedondo && seleccionados.length > 0 && (
                    <p className="text-blue-300 text-sm mt-2">
                      {seleccionados.length} pasajero{seleccionados.length !== 1 ? 's' : ''} • ${toSafeInt(viaje?.precio)} c/u
                    </p>
                  )}
                </div>
              </div>

              {clientSecret && stripePromise ? (
                <div className="bg-white p-4 rounded-xl text-black animate-in slide-in-from-bottom-4 border-2 border-blue-500">
                  <Elements stripe={stripePromise} options={{ clientSecret }}>
                    <CheckoutForm total={totalPagar} />
                  </Elements>
                </div>
              ) : (
                <button
                  onClick={handleCheckout}
                  disabled={(esRedondo ? (seleccionados.length === 0 || seleccionadosRegreso.length === 0) : seleccionados.length === 0) || procesando}
                  className="w-full bg-emerald-500 hover:bg-emerald-400 py-4 rounded-xl font-bold flex justify-center items-center gap-2 disabled:bg-slate-700 transition-colors"
                >
                  {procesando
                    ? <span className="animate-pulse">Cargando pasarela...</span>
                    : <><FaCreditCard /> PAGAR ${totalPagar} MXN</>
                  }
                </button>
              )}
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}